import { Box, Flex, Radio, Tag, Text } from "@chakra-ui/react"
import { useRouter } from "next/router"
import React, { useState } from "react"
import { fhAnalyticsUserActions, keys } from "../../../../../../../common/constants/constants"
import { filterConstants } from "../../../../../../../common/constants/search-constants"
import useKeyPress from "../../../../../../../common/hooks/useKeyPress"
import { useUrlParams } from "../../../../../../../common/lib/UrlParamsContext"
import sendFhAnalytics from "../../../../../../../common/lib/fredhopper"
import { FhResponseData } from "../../../../../../../common/types/search"
import FilterFormFooter from "../FilterFormFooter/FilterFormFooter"
import FilterZeroResults from "../FilterZeroResults/FilterZeroResults"

type Props = {
  filteredFiltersection: FhResponseData
  searchFiltersection?: FhResponseData // Comes from TypeHeadSearch component, the filteres items
  isSearchEnabled: boolean
  basetype: FhResponseData
  breadcrumbFilters: FhResponseData
  isOverlayFilters: boolean
  closeFiltersOverlay: () => void
  handleOnClose: () => void
  rid: string
  facetId: string
}

const RadioListForm = ({
  filteredFiltersection,
  searchFiltersection,
  isSearchEnabled,
  basetype,
  breadcrumbFilters,
  isOverlayFilters,
  closeFiltersOverlay,
  handleOnClose,
  rid,
  facetId,
}: Props) => {
  const { locale } = useRouter()
  const renderedFiltersection = isSearchEnabled ? searchFiltersection : filteredFiltersection
  const { setUrlParams } = useUrlParams()
  const defaultSelectedFilter = renderedFiltersection.find((filter: FhResponseData) => filter.selected)
  const defaultNr = defaultSelectedFilter?.nr || 0
  const [isFormNotTouched, setIsFormNotTouched] = useState<boolean>(true)
  const [selectedProductsCount, setSelectedProductsCount] = useState<number>(defaultNr)
  const [selectedUrlParams, setSelectedUrlParams] = useState<string | null>(
    defaultSelectedFilter?.link?.url_params || null
  )
  const [selectedFacetValue, setSelectedFacetValue] = useState(defaultSelectedFilter?.value?.value || null)
  const isZeroResults = searchFiltersection?.length === 0
  const isAtLeastOneProductSelected = selectedProductsCount > 0
  const isSelectedProductsCountShown = basetype !== filterConstants.SET && isAtLeastOneProductSelected

  // Close button is active if form was not touched or no radio is checked
  const isCloseButtonActive = isFormNotTouched || selectedProductsCount === 0
  const isResetAllRendered = isAtLeastOneProductSelected && !isZeroResults

  useKeyPress(keys.ENTER, () => checkSingleRemainingRadioAndSubmitForm(), renderedFiltersection, selectedUrlParams)

  const checkSingleRemainingRadioAndSubmitForm = () => {
    if (isSearchEnabled && renderedFiltersection.length === 1) {
      const { url_params } = renderedFiltersection[0].link
      setUrlParams(url_params)
      if (isOverlayFilters) closeFiltersOverlay()
      handleOnClose()
      return
    }

    if (selectedUrlParams) setUrlParams(selectedUrlParams)

    if (isOverlayFilters) closeFiltersOverlay()

    handleOnClose()
  }

  const radioFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    nr: number,
    url_params: string,
    facetValue: string
  ) => {
    setIsFormNotTouched(false)
    setSelectedProductsCount(nr)
    setSelectedUrlParams(url_params)
    setSelectedFacetValue(facetValue)
  }

  // console.log(`rid: ${rid}`)
  const radioListFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!isCloseButtonActive) {
      setUrlParams(selectedUrlParams)

      // After submit close the overlay
      if (isOverlayFilters) closeFiltersOverlay()
      sendFhAnalytics({
        locale,
        action: fhAnalyticsUserActions.FACETCLICK,
        sourceId: rid,
        facetId,
        facets: {
          prev: {},
          current: {
            [selectedFacetValue]: true,
          },
        },
      })
    }

    handleOnClose()
  }

  const radioListFormReset = () => {
    if (defaultSelectedFilter) {
      const defaultSelectedFilterName = defaultSelectedFilter?.link?.name
      const removeCrumb = breadcrumbFilters.find(
        (crumb: FhResponseData) => crumb.name.value === defaultSelectedFilterName
      )

      setUrlParams(removeCrumb.link[0].url_params)
    }

    handleOnClose()
  }

  return (
    <Box width="100%">
      <form onSubmit={(e) => radioListFormSubmit(e)}>
        <Flex direction="column" gap={isOverlayFilters ? "2" : "0"}>
          {renderedFiltersection.map((filter: FhResponseData) => {
            const { link, value, nr } = filter
            const isChecked = selectedUrlParams === link.url_params

            return (
              <Flex
                as={Flex}
                key={JSON.stringify(filter)}
                align="center"
                justify="space-between"
                w="100%"
                h="10"
                pl={isOverlayFilters ? { base: "6", sm: "8" } : "2"}
                pr={isOverlayFilters ? { base: "6", sm: "8" } : "3"}
                cursor="pointer"
                _hover={{
                  "@media (hover: hover) and (pointer: fine)": {
                    bgColor: "gray.200",
                    ".chakra-radio__control": { borderColor: "black", _checked: { bg: "gray.200" } },
                  },
                }}
              >
                <Radio
                  isChecked={isChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    radioFilterChange(e, nr, link.url_params, value?.value)
                  }
                  value={link.url_params}
                  name="filter-radio"
                  id={value.value}
                  flexGrow={1}
                >
                  <Flex align="center" justify="space-between" display="inline-flex" w="100%">
                    {link.name}
                    <Tag borderRadius="full" bgColor="gray.200" h="6">
                      <Text color="gray.900" fontSize="xs" fontWeight="medium">
                        {nr}
                      </Text>
                    </Tag>
                  </Flex>
                </Radio>
              </Flex>
            )
          })}
        </Flex>

        {isZeroResults && <FilterZeroResults />}

        <FilterFormFooter
          isBoxShadow={renderedFiltersection.length > 7}
          isSelectedProductsCountShown={isSelectedProductsCountShown}
          selectedProductsCount={selectedProductsCount}
          resetAllClickFunction={radioListFormReset}
          isCloseButtonActive={isCloseButtonActive}
          isResetAllRendered={isResetAllRendered}
        />
      </form>
    </Box>
  )
}

export default RadioListForm
